/**
 * Sawyer #1296 — the pure half of "Add this to the owner's notes?".
 *
 * Sawyer (HTHD, 10-26): *"it should pop up a window that will ask if we want
 * to put that on the owner notes because they aren't always applicable."*
 * 10-29: ask BOTH when the desk accepts the reservation AND at check-in.
 *
 * THE RULE, stated once (every surface asks through `ownerNoteCandidates`):
 *
 *   Ask about a booking's note when, and only when,
 *     1. the staffer may write client notes (`customers.notes:write:all`);
 *     2. the booking has a note with words in it (Gingr label stripped, the
 *        same rule /today's `bookingNote` is built from);
 *     3. the booking has an owner;
 *     4. the SERVER says the note is not in the owner's notes yet
 *        (`=== false`). Absent (an older server) or null (the read failed)
 *        never asks: a prompt the server cannot answer is a lie, and the
 *        accept / check-in it rides on must stand alone;
 *     5. it was not added from this page already (the server's flag is a
 *        page-load old until the next refetch);
 *     6. it is not a service leg (a leg rides its visit; the desk never
 *        checks one in on its own).
 *
 *   "Not now" is NOT remembered: a note declined at accept is asked about
 *   again at check-in (Sawyer said both). Only a note actually added stops
 *   the asking, and that fact lives on the server.
 *
 * Bulk accept / "Check in all" hand every row to ONE call, and the dialog
 * lists each note with its own choice: never N stacked dialogs.
 */
import { displayBookingNote } from '@ferro/core/modules/booking/booking-note.js';
import type { AdminBooking } from '../../lib/api.js';
import type { DashboardArrival } from '../dashboard/dashboard-api.js';
import type { PendingReservation } from '../booking/confirm-pending.js';

/** One note the desk may keep on its owner. */
export interface OwnerNoteCandidate {
  readonly bookingId: string;
  readonly customerId: string;
  /** "Hazel & Hank" is built from these. */
  readonly dogNames: ReadonlyArray<string>;
  readonly ownerName: string | null;
  /** The note as staff read it (Gingr label stripped, trimmed). */
  readonly note: string;
}

/** What any surface knows about a booking it just accepted or checked in. */
export interface BookingNoteFacts {
  readonly bookingId: string;
  readonly customerId: string | null | undefined;
  readonly dogNames?: ReadonlyArray<string> | null;
  readonly ownerName?: string | null;
  /** The booking's note, raw or already stripped (stripping twice is a no-op). */
  readonly note: string | null | undefined;
  /** The server's flag; only `false` asks. */
  readonly inOwnerNotes: boolean | null | undefined;
  readonly isLeg?: boolean | null;
}

export interface CandidateOptions {
  /** `customers.notes:write:all` for the signed-in staffer. */
  readonly canWriteClientNotes: boolean;
  /** Booking ids whose note was added from this page since the last refetch. */
  readonly addedHere?: ReadonlySet<string>;
}

export function ownerNoteCandidates(
  rows: ReadonlyArray<BookingNoteFacts>,
  opts: CandidateOptions,
): OwnerNoteCandidate[] {
  if (!opts.canWriteClientNotes) return [];
  const seen = new Set<string>();
  const out: OwnerNoteCandidate[] = [];
  for (const row of rows) {
    if (seen.has(row.bookingId)) continue;
    if (row.inOwnerNotes !== false) continue;
    if (row.isLeg) continue;
    if (opts.addedHere?.has(row.bookingId)) continue;
    const customerId = typeof row.customerId === 'string' ? row.customerId.trim() : '';
    if (customerId.length === 0) continue;
    const note = displayBookingNote(row.note);
    if (note === null) continue;
    seen.add(row.bookingId);
    out.push({
      bookingId: row.bookingId,
      customerId,
      dogNames: (row.dogNames ?? []).filter((n) => n.trim().length > 0),
      ownerName: row.ownerName?.trim() ? row.ownerName.trim() : null,
      note,
    });
  }
  return out;
}

/**
 * Merge newly offered candidates into an open prompt (a dog confirmed later
 * in the same flow joins the list instead of opening a second dialog).
 */
export function mergeCandidates(
  current: ReadonlyArray<OwnerNoteCandidate>,
  incoming: ReadonlyArray<OwnerNoteCandidate>,
): OwnerNoteCandidate[] {
  const ids = new Set(current.map((c) => c.bookingId));
  return [...current, ...incoming.filter((c) => !ids.has(c.bookingId))];
}

/** The permission the prompt's Yes needs (the server checks the same string). */
export const CLIENT_NOTES_WRITE_PERMISSION = 'customers.notes:write:all';

/** The desk's words for a failed add: what happened and that nothing was lost. */
export function ownerNoteErrorCopy(code: string | null | undefined, status: number | null | undefined): string {
  if (code === 'booking_note_empty') return 'This booking no longer has a note to add.';
  if (status === 403) return "You can't add client notes. The note stays on this booking.";
  return "Couldn't add it to the owner's notes. The note is still on this booking. Try again.";
}

// ── Adapters: the three payload shapes the desk acts on ───────────────────

/** A `/today` row (Front Desk, dashboard arrivals). */
export function factsFromArrival(row: DashboardArrival): BookingNoteFacts {
  return {
    bookingId: row.bookingId,
    customerId: row.customerId,
    dogNames: row.dogNames,
    ownerName: row.customerName,
    note: row.bookingNote,
    inOwnerNotes: row.bookingNoteInOwnerNotes,
    isLeg: row.isLeg ?? false,
  };
}

/** A `/schedule` booking (the Bookings page). */
export function factsFromAdminBooking(b: AdminBooking): BookingNoteFacts {
  return {
    bookingId: b.id,
    customerId: b.customer_id,
    dogNames: b.dog_names ?? [],
    ownerName: b.customer_name ?? null,
    note: b.notes,
    inOwnerNotes: b.booking_note_in_owner_notes,
    isLeg: Boolean(b.parent_booking_id),
  };
}

/** A reservation the confirm flow just accepted. */
export function factsFromPending(item: PendingReservation, ownerName: string | null): BookingNoteFacts {
  return {
    bookingId: item.bookingId,
    customerId: item.customerId,
    dogNames: item.dogNames,
    ownerName,
    note: item.notes,
    inOwnerNotes: item.bookingNoteInOwnerNotes,
  };
}
